import type { OfferAppliesTo, OfferDiscountType, OfferKind } from "@luckymenu/core";
import { localParts } from "@luckymenu/core/client";
import type { DiscountInput, DiscountRule } from "./discount";
import { assertBasisPoints, assertPaise, mulDivRound } from "./integer";

/**
 * Offers (P9-T02): which run at a given moment, the best price of an item
 * and the best bill offer. Pure and free of zod, so the storefront's browser
 * code uses it too (`@luckymenu/billing-core/offers`), and the price a diner
 * sees on the menu, in the cart and on the bill is the same number.
 *
 * Rules (docs/architecture/ordering-billing-gst.md, Offers):
 * - Offers do not stack. An item gets at most one item offer, the one that
 *   takes the most off; a bill gets at most one bill offer or coupon, the
 *   one that takes the most off. Ties go to the offer listed first (the
 *   owner's order).
 * - An item offer works on the item's own price (its size's price), never
 *   on add-ons, and is decided per unit, so a line's discount is the unit
 *   discount × quantity: `percent` takes round(price × bp ÷ 10000), capped
 *   at `maxDiscountPaise`; `flat` takes `value`; `fixed_price` makes the
 *   unit cost `value`. Never more than the price, never below zero.
 * - A bill offer works on the bill after item offers. It needs that value to
 *   reach `minOrderPaise`. `percent` is capped at `maxDiscountPaise`.
 * - Schedules are read in the restaurant's time zone: from `startsAt`
 *   (included) to `endsAt` (not included), on `daysOfWeek` (empty: every
 *   day), between `timeFrom` (included) and `timeTo` (not included). A
 *   window past midnight (22:00 to 02:00) belongs to the day it starts on.
 */

/** What selection needs of an offer. Matches `Offer` and `PublicOffer` in core. */
export interface OfferRule {
	id: string;
	name: string;
	kind: OfferKind;
	discountType: OfferDiscountType;
	value: number;
	minOrderPaise: number | null;
	maxDiscountPaise: number | null;
	appliesTo: OfferAppliesTo;
	itemIds: readonly string[];
	categoryIds: readonly string[];
	/** ISO instants or null. */
	startsAt: string | null;
	endsAt: string | null;
	/** ISO weekdays, 1 Monday to 7 Sunday. Empty: every day. */
	daysOfWeek: readonly number[];
	/** "HH:MM" or null, both or neither. */
	timeFrom: string | null;
	timeTo: string | null;
	/** Staff lists carry the switch; diners' lists hold only offers that are on. */
	isActive?: boolean;
}

export type OfferStatus = "active" | "scheduled" | "ended" | "off";

/** Whether the time-of-day window and weekdays allow `at`. */
function inWindow(offer: OfferRule, at: Date, timeZone: string): boolean {
	const { isoDay, time } = localParts(at, timeZone);
	const dayOk = (day: number) => offer.daysOfWeek.length === 0 || offer.daysOfWeek.includes(day);
	const { timeFrom: from, timeTo: to } = offer;
	if (from === null || to === null) return dayOk(isoDay);
	if (from < to) return time >= from && time < to && dayOk(isoDay);
	// Past midnight: the evening part is today's, the early part yesterday's.
	if (time >= from) return dayOk(isoDay);
	if (time < to) return dayOk(isoDay === 1 ? 7 : isoDay - 1);
	return false;
}

/** Whether an offer applies at `at`: switched on, within its dates, days and hours. */
export function isOfferLive(offer: OfferRule, at: Date, timeZone: string): boolean {
	if (offer.isActive === false) return false;
	const t = at.getTime();
	if (offer.startsAt !== null && t < Date.parse(offer.startsAt)) return false;
	if (offer.endsAt !== null && t >= Date.parse(offer.endsAt)) return false;
	return inWindow(offer, at, timeZone);
}

/**
 * For More > Offers: `off` when switched off, `ended` after its end,
 * `active` when it applies now, otherwise `scheduled` (it starts later, or
 * runs on other days or hours).
 */
export function offerStatus(offer: OfferRule, at: Date, timeZone: string): OfferStatus {
	if (offer.isActive === false) return "off";
	if (offer.endsAt !== null && at.getTime() >= Date.parse(offer.endsAt)) return "ended";
	return isOfferLive(offer, at, timeZone) ? "active" : "scheduled";
}

/** The offers that apply at `at`, in the order given. */
export function liveOffers<T extends OfferRule>(
	offers: readonly T[],
	at: Date,
	timeZone: string,
): T[] {
	return offers.filter((o) => isOfferLive(o, at, timeZone));
}

// ---------------------------------------------------------------------------
// Item offers.
// ---------------------------------------------------------------------------

/** Where an item sits on the menu, for offers on categories. */
export interface OfferItemRef {
	itemId: string;
	categoryId: string;
}

export function offerAppliesToItem(offer: OfferRule, item: OfferItemRef): boolean {
	if (offer.kind !== "item_discount") return false;
	switch (offer.appliesTo) {
		case "all":
			return true;
		case "items":
			return offer.itemIds.includes(item.itemId);
		case "categories":
			return offer.categoryIds.includes(item.categoryId);
	}
}

/** How much an item offer takes off one unit priced `unitPricePaise` (add-ons aside). */
export function itemOfferUnitDiscount(offer: OfferRule, unitPricePaise: number): number {
	assertPaise(unitPricePaise, "unit price");
	let off: number;
	switch (offer.discountType) {
		case "percent":
			assertBasisPoints(offer.value, "offer percent");
			off = mulDivRound(unitPricePaise, offer.value, 10000);
			if (offer.maxDiscountPaise !== null) off = Math.min(off, offer.maxDiscountPaise);
			break;
		case "flat":
			off = offer.value;
			break;
		case "fixed_price":
			off = unitPricePaise - offer.value;
			break;
	}
	return Math.max(0, Math.min(off, unitPricePaise));
}

export interface ItemOfferMatch<T extends OfferRule = OfferRule> {
	offer: T;
	/** Off each unit. More than zero. */
	unitDiscountPaise: number;
}

/**
 * The item offer that gives this item at this price the lowest price among
 * `offers` (live ones only: filter with `liveOffers` or pass `at`), or null.
 */
export function bestItemOffer<T extends OfferRule>(
	offers: readonly T[],
	item: OfferItemRef,
	unitPricePaise: number,
	when?: { at: Date; timeZone: string },
): ItemOfferMatch<T> | null {
	let best: ItemOfferMatch<T> | null = null;
	for (const offer of offers) {
		if (!offerAppliesToItem(offer, item)) continue;
		if (when && !isOfferLive(offer, when.at, when.timeZone)) continue;
		const unitDiscountPaise = itemOfferUnitDiscount(offer, unitPricePaise);
		if (unitDiscountPaise > 0 && (!best || unitDiscountPaise > best.unitDiscountPaise)) {
			best = { offer, unitDiscountPaise };
		}
	}
	return best;
}

/** An item offer as a line discount for `calculateBill`: the unit discount × quantity. */
export function itemOfferDiscount(
	offer: { id: string; name: string },
	unitDiscountPaise: number,
	quantity: number,
): DiscountInput {
	return {
		rule: { type: "amount", paise: unitDiscountPaise * quantity },
		label: offer.name,
		reason: null,
		offerId: offer.id,
	};
}

// ---------------------------------------------------------------------------
// Bill offers and coupons.
// ---------------------------------------------------------------------------

/** A bill offer's rule for `calculateBill`. */
export function billOfferRule(offer: OfferRule): DiscountRule {
	return offer.discountType === "percent"
		? { type: "percent", bp: offer.value, maxPaise: offer.maxDiscountPaise }
		: { type: "amount", paise: offer.value };
}

/** A bill offer as the bill discount for `calculateBill`. */
export function billOfferDiscount(offer: OfferRule): DiscountInput {
	return { rule: billOfferRule(offer), label: offer.name, reason: null, offerId: offer.id };
}

/**
 * What a bill offer takes off a bill worth `valuePaise` after item offers:
 * 0 below its minimum order, never more than the value.
 */
export function billOfferAmount(offer: OfferRule, valuePaise: number): number {
	assertPaise(valuePaise, "bill value");
	if (offer.kind === "item_discount" || offer.discountType === "fixed_price") return 0;
	if (offer.minOrderPaise !== null && valuePaise < offer.minOrderPaise) return 0;
	const rule = billOfferRule(offer);
	if (rule.type === "amount") return Math.min(rule.paise, valuePaise);
	let off = mulDivRound(valuePaise, rule.bp, 10000);
	if (rule.maxPaise !== null) off = Math.min(off, rule.maxPaise);
	return Math.min(off, valuePaise);
}

export interface BillOfferMatch<T extends OfferRule = OfferRule> {
	offer: T;
	amountPaise: number;
}

/**
 * The bill offers (and coupons) among `offers` that a bill worth `valuePaise`
 * gets, most off first; ties keep the given order. Pass live offers only.
 */
export function eligibleBillOffers<T extends OfferRule>(
	offers: readonly T[],
	valuePaise: number,
): BillOfferMatch<T>[] {
	return offers
		.filter((o) => o.kind !== "item_discount")
		.map((offer, index) => ({ offer, amountPaise: billOfferAmount(offer, valuePaise), index }))
		.filter((m) => m.amountPaise > 0)
		.sort((a, b) => b.amountPaise - a.amountPaise || a.index - b.index)
		.map(({ offer, amountPaise }) => ({ offer, amountPaise }));
}

/** The bill offer that takes the most off, or null. */
export function bestBillOffer<T extends OfferRule>(
	offers: readonly T[],
	valuePaise: number,
): BillOfferMatch<T> | null {
	return eligibleBillOffers(offers, valuePaise)[0] ?? null;
}

export interface OfferNudge<T extends OfferRule = OfferRule> {
	offer: T;
	/** How much more the bill needs to reach the offer's minimum. */
	morePaise: number;
}

/**
 * "Add ₹60 more to get 10% off" (P9-T14): among bill offers (not coupons)
 * whose minimum the bill has not reached, the one closest to it, but only
 * when it would take off more than the bill gets now. Pass live offers only.
 */
export function nearestBillOffer<T extends OfferRule>(
	offers: readonly T[],
	valuePaise: number,
): OfferNudge<T> | null {
	const current = bestBillOffer(offers, valuePaise)?.amountPaise ?? 0;
	let best: OfferNudge<T> | null = null;
	for (const offer of offers) {
		if (offer.kind !== "bill_discount" || offer.minOrderPaise === null) continue;
		if (valuePaise >= offer.minOrderPaise) continue;
		if (billOfferAmount(offer, offer.minOrderPaise) <= current) continue;
		const morePaise = offer.minOrderPaise - valuePaise;
		if (!best || morePaise < best.morePaise) best = { offer, morePaise };
	}
	return best;
}
